/*
 * Cron is the storage format, not the editing format. Nobody should have to
 * write "0 7 * * 1" to say "every Monday at 07:00", so the UI edits a small
 * structured value and this module translates in both directions. Anything a
 * form can't express round-trips untouched as a raw expression.
 */

export type ScheduleFrequency =
  | 'manual'
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'advanced';

export interface Schedule {
  frequency: ScheduleFrequency;
  /** 'HH:MM' in Europe/Brussels. */
  time: string;
  /** Cron day-of-week: 0 = Sunday. */
  weekday: number;
  day_of_month: number;
  /** 'YYYY-MM-DD' — which week a bi-weekly schedule counts from. */
  anchor: string | null;
  /** The raw expression, used when frequency is 'advanced'. */
  cron: string;
}

export interface StoredSchedule {
  schedule_cron: string | null;
  interval_anchor: string | null;
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const WEEKDAY_OPTIONS = WEEKDAYS.map((label, value) => ({ value, label }));

export const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'manual', label: 'Manual only' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'biweekly', label: 'Every other week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'advanced', label: 'Advanced (cron)' },
];

export function emptySchedule(): Schedule {
  return {
    frequency: 'manual',
    time: '07:00',
    weekday: 1,
    day_of_month: 1,
    anchor: null,
    cron: '',
  };
}

/** Turn what the form holds into the two columns the routine stores. */
export function toStoredSchedule(schedule: Schedule): StoredSchedule {
  const [hour, minute] = splitTime(schedule.time);

  switch (schedule.frequency) {
    case 'manual':
      return { schedule_cron: null, interval_anchor: null };
    case 'daily':
      return { schedule_cron: `${minute} ${hour} * * *`, interval_anchor: null };
    case 'weekly':
      return {
        schedule_cron: `${minute} ${hour} * * ${schedule.weekday}`,
        interval_anchor: null,
      };
    case 'biweekly':
      return {
        schedule_cron: `${minute} ${hour} * * ${schedule.weekday}`,
        interval_anchor: schedule.anchor,
      };
    case 'monthly':
      return {
        schedule_cron: `${minute} ${hour} ${schedule.day_of_month} * *`,
        interval_anchor: null,
      };
    case 'advanced':
      return {
        schedule_cron: schedule.cron.trim() || null,
        interval_anchor: schedule.anchor,
      };
  }
}

/** Read a stored schedule back into the form, falling back to raw cron. */
export function fromStoredSchedule(stored: StoredSchedule): Schedule {
  const base = emptySchedule();
  const expression = stored.schedule_cron?.trim();
  if (!expression) return base;

  const fields = expression.split(/\s+/);
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  const advanced: Schedule = {
    ...base,
    frequency: 'advanced',
    cron: expression,
    anchor: stored.interval_anchor,
  };

  if (fields.length !== 5 || month !== '*') return advanced;
  if (!isPlainNumber(minute) || !isPlainNumber(hour)) return advanced;

  const schedule: Schedule = {
    ...advanced,
    time: `${pad(Number(hour))}:${pad(Number(minute))}`,
  };

  if (dayOfMonth === '*' && dayOfWeek === '*') {
    return { ...schedule, frequency: 'daily' };
  }
  if (dayOfMonth === '*' && isPlainNumber(dayOfWeek)) {
    return {
      ...schedule,
      frequency: stored.interval_anchor ? 'biweekly' : 'weekly',
      weekday: Number(dayOfWeek) % 7,
    };
  }
  if (dayOfWeek === '*' && isPlainNumber(dayOfMonth)) {
    return { ...schedule, frequency: 'monthly', day_of_month: Number(dayOfMonth) };
  }

  return advanced;
}

/** A one-line, human-readable schedule for the routines overview. */
export function describeSchedule(stored: StoredSchedule): string {
  const schedule = fromStoredSchedule(stored);
  const at = `at ${schedule.time}`;

  switch (schedule.frequency) {
    case 'manual':
      return 'Manual only';
    case 'daily':
      return `Every day ${at}`;
    case 'weekly':
      return `Every ${WEEKDAYS[schedule.weekday]} ${at}`;
    case 'biweekly':
      return `Every other ${WEEKDAYS[schedule.weekday]} ${at}, from ${schedule.anchor}`;
    case 'monthly':
      return `Day ${schedule.day_of_month} of each month ${at}`;
    case 'advanced':
      return schedule.cron;
  }
}

function splitTime(time: string): [number, number] {
  const [hour, minute] = time.split(':');
  return [Number(hour) || 0, Number(minute) || 0];
}

function isPlainNumber(field: string | undefined): boolean {
  return field !== undefined && /^\d+$/.test(field);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
